import React, { createContext, useState, useContext, useEffect } from 'react';
import { ethers } from 'ethers';

const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const [address, setAddress] = useState(null);
  const [balance, setBalance] = useState(null);
  const [network, setNetwork] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [stakedAssets, setStakedAssets] = useState([]);
  const [rewards, setRewards] = useState('0');

  // Mock assets for demo
  const mockAssets = [
    {
      id: 'nft-1',
      name: 'Bored Ape #1234',
      type: 'nft',
      network: 'ethereum',
      tokenId: '1234',
      image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&h=400&fit=crop',
      estimatedValue: 45000,
      stakingStatus: 'earning',
      apy: 12.5,
      estimatedDailyReward: 0.0234,
      rewardProgress: 0.65,
      stakedAt: '2024-01-15T10:00:00Z'
    },
    {
      id: 'token-1',
      name: 'Ethereum',
      type: 'token',
      network: 'ethereum',
      symbol: 'ETH',
      balance: '2.5',
      image: 'https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=400&h=400&fit=crop',
      estimatedValue: 8750,
      stakingStatus: 'staked',
      apy: 8.2,
      stakedAt: '2024-01-20T14:30:00Z'
    },
    {
      id: 'token-2',
      name: 'Polygon',
      type: 'token',
      network: 'polygon',
      symbol: 'MATIC',
      balance: '1250.75',
      image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=400&fit=crop',
      estimatedValue: 1063,
      stakingStatus: 'earning',
      apy: 18.5,
      estimatedDailyReward: 0.0567,
      rewardProgress: 0.42,
      stakedAt: '2024-01-18T09:15:00Z'
    }
  ];

  useEffect(() => {
    // Set mock data
    setAssets(mockAssets);
    setRewards('25.50');
  }, []);

  const connectWallet = async () => {
    try {
      setIsLoading(true);
      
      if (typeof window.ethereum !== 'undefined') {
        const provider = new ethers.BrowserProvider(window.ethereum);
        await provider.send('eth_requestAccounts', []);
        const signer = await provider.getSigner();
        const address = await signer.getAddress();
        const balance = await provider.getBalance(address);
        const network = await provider.getNetwork();

        setAddress(address);
        setBalance(ethers.formatEther(balance));
        setChainId(Number(network.chainId));
        setNetwork(network.name);
        setIsConnected(true);
        
        return { address, balance: ethers.formatEther(balance), chainId: Number(network.chainId) };
      } else {
        // Mock connection for demo
        const mockAddress = '0x742d35Cc6634C0532925a3b8D398C4C00E064533';
        setAddress(mockAddress);
        setBalance('2.5');
        setChainId(11155111);
        setNetwork('sepolia');
        setIsConnected(true);
        
        return { address: mockAddress, balance: '2.5', chainId: 11155111 };
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const disconnectWallet = () => {
    setAddress(null);
    setBalance(null);
    setNetwork(null);
    setChainId(null);
    setIsConnected(false);
  };

  const switchNetwork = async (targetChainId) => {
    try {
      setIsLoading(true);
      
      if (typeof window.ethereum !== 'undefined') {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${targetChainId.toString(16)}` }],
        });
      }
      
      setChainId(targetChainId);
      return true;
    } catch (error) {
      console.error('Failed to switch network:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    // Mock refresh
    setAssets(mockAssets);
    setRewards('25.50');
  };

  const contextValue = {
    address,
    balance,
    network,
    chainId,
    isConnected,
    isLoading,
    assets,
    stakedAssets,
    rewards,
    connectWallet,
    disconnectWallet,
    switchNetwork,
    refreshData
  };

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};